import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useCart } from '../../hooks/useCart';
import { useCurrency } from '../../hooks/useCurrency';
import { useLanguage } from '../../hooks/useLanguage';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { getDisplayPrice, getSecondaryPrice } from '../../lib/currency';
import VpnSeoJsonLd from './components/SeoJsonLd';

export default function VPNPage() {
  const { addItem } = useCart();
  const { currency } = useCurrency();
  const { t } = useLanguage();
  const [addedId, setAddedId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { containerRef, getAnimationClass, getAnimationStyle } = useScrollAnimation();

  const vpnProducts = [
    {
      id: 'adguard-1mes',
      name: 'AdGuard VPN 1 Mes - 1 Dispositivo',
      price: 30500,
      oldPrice: 45000,
      discount: '-32%',
      image: 'https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/6b8688c129017d7e175bddd371672b98.png',
      badge: 'Sale',
      salesCount: 78,
    },
    {
      id: 'adguard-1year',
      name: 'AdGuard VPN 1 Año - 5 Dispositivos',
      price: 21950,
      oldPrice: 45000,
      discount: '-51%',
      image: 'https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/6b8688c129017d7e175bddd371672b98.png',
      badge: 'Sale',
      priceNote: t('product.monthly'),
      multiplyBy: 12,
      licenseText: `5 ${t('product.licenses')} x 12 ${t('product.months')} (1 ${t('product.year')})`,
      bestOption: false,
      salesCount: 167,
    },
    {
      id: 'adguard-2years',
      name: 'AdGuard VPN 2 Años - 10 Dispositivos',
      price: 14950,
      oldPrice: 45000,
      discount: '-71%',
      image: 'https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/6b8688c129017d7e175bddd371672b98.png',
      badge: 'Sale',
      priceNote: t('product.monthly'),
      multiplyBy: 24,
      licenseText: `10 ${t('product.licenses')} x 24 ${t('product.months')} (2 ${t('product.years')})`,
      bestOption: true,
      salesCount: 283,
    },
  ];

  const handleAddToCart = (product: typeof vpnProducts[0]) => {
    try {
      const quantity = product.multiplyBy || 1;
      for (let i = 0; i < quantity; i++) {
        addItem({
          id: product.id,
          name: product.name,
          type: product.type,
          price: product.price,
          oldPrice: product.oldPrice,
          image: product.image,
        });
      }
      setAddedId(product.id);
      setTimeout(() => setAddedId(null), 1500);
    } catch (error) {
      console.error('Error adding item to cart:', error);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <VpnSeoJsonLd />
      <Header />

      {/* Hero */}
      <section className="relative h-[200px] sm:h-[250px] md:h-[300px] bg-gradient-to-r from-emerald-600 to-teal-500 overflow-hidden">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/14d41a1177cbc5603a48356986d43ac0.jpg"
            alt="VPN Security"
            className="w-full h-full object-cover opacity-25"
          />
        </div>
        <div className="relative container mx-auto px-4 h-full flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-3 sm:gap-4">
          <div className="text-white text-center sm:text-left">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-4">{t('vpn.title')}</h1>
            <p className="text-sm sm:text-base md:text-xl max-w-2xl">
              {t('vpn.subtitle')}
            </p>
          </div>
          <Link
            to="/comparar-vpn"
            className="bg-white text-emerald-600 px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap text-sm sm:text-base"
          >
            {t('product.compare_vpn')}
          </Link>
        </div>
      </section>

      {/* Products */}
      <main className="flex-1 py-8 sm:py-12 md:py-16">
        <div className="container mx-auto px-3 sm:px-4">
          <div
            ref={containerRef}
            className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6"
            data-product-shop
          >
            {vpnProducts.map((product, index) => {
              const display = getDisplayPrice(product.price, product.oldPrice, currency);
              const secondary = getSecondaryPrice(product.price, product.oldPrice, currency);
              return (
                <div
                  key={product.id}
                  data-anim-index={index}
                  style={getAnimationStyle(index)}
                  className={`bg-white border rounded-lg overflow-hidden hover:shadow-lg transition-all duration-500 ease-out group cursor-pointer relative ${
                    product.bestOption
                      ? 'border-emerald-500 border-2 ring-2 ring-emerald-100'
                      : 'border-gray-200'
                  } ${getAnimationClass(index)}`}
                >
                  {product.bestOption && (
                    <div className="bg-emerald-500 text-white text-center py-1 sm:py-1.5 text-[10px] sm:text-sm font-bold tracking-wide">
                      <i className="ri-star-fill mr-1"></i>
                      <span className="hidden sm:inline">{t('product.best_option')}</span>
                      <span className="sm:hidden">{t('product.best_short')}</span>
                    </div>
                  )}
                  <div className="relative">
                    {product.badge && (
                      <span className="absolute top-2 sm:top-3 left-2 sm:left-3 bg-red-500 text-white text-[10px] sm:text-xs font-semibold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded whitespace-nowrap">
                        {product.badge}
                      </span>
                    )}
                    {product.discount && (
                      <span className="absolute top-2 sm:top-3 right-2 sm:right-3 bg-red-500 text-white text-[10px] sm:text-xs font-semibold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded whitespace-nowrap">
                        {product.discount}
                      </span>
                    )}
                    <div className="w-full h-28 sm:h-36 md:h-48 bg-gray-50 flex items-center justify-center p-3 sm:p-4">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                      />
                    </div>
                  </div>
                  <div className="p-3 sm:p-4">
                    <h3 className="text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2 line-clamp-2 h-8 sm:h-10">
                      {product.name}
                    </h3>
                    <div className="flex items-center gap-1 mb-1.5 sm:mb-2">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-yellow-400 text-[10px] sm:text-sm"></i>
                      ))}
                      <span className="text-[9px] sm:text-xs text-gray-500 ml-0.5">(5.0)</span>
                    </div>
                    <div className="flex items-center gap-1 mb-1.5 sm:mb-2">
                      <i className="ri-shopping-bag-line text-[10px] sm:text-xs text-orange-500"></i>
                      <span className="text-[10px] sm:text-xs text-gray-500">{product.salesCount.toLocaleString('es-CO')} vendidos</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2">
                      <span className="text-sm sm:text-lg font-bold text-gray-900">{display.main}</span>
                      {display.oldMain && (
                        <span className="text-[10px] sm:text-sm text-gray-500 line-through">{display.oldMain}</span>
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 mt-0.5">
                      <p className="text-[10px] sm:text-xs text-emerald-600 font-medium">
                        ≈ {secondary.current}
                      </p>
                      {secondary.old && (
                        <p className="text-[10px] sm:text-xs text-gray-400 line-through hidden sm:block">
                          {secondary.old}
                        </p>
                      )}
                    </div>
                    {product.priceNote && (
                      <p className="text-[10px] sm:text-xs text-gray-600 mt-0.5 sm:mt-1">{product.priceNote}</p>
                    )}
                    {product.multiplyBy > 1 && (
                      <p className="text-[10px] sm:text-xs text-emerald-600 font-medium mt-0.5 sm:mt-1 hidden sm:block">
                        <i className="ri-group-line mr-1"></i>
                        {t('product.includes')}{' '}
                        {product.licenseText ||
                          `${product.multiplyBy} ${t('product.licenses')} (x${product.multiplyBy})`}
                      </p>
                    )}
                    <button
                      onClick={() => handleAddToCart(product)}
                      className={`w-full mt-2 sm:mt-4 py-1.5 sm:py-2 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap text-xs sm:text-sm ${
                        addedId === product.id
                          ? 'bg-green-500 text-white'
                          : product.bestOption
                          ? 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-md shadow-emerald-200'
                          : 'bg-emerald-600 text-white hover:bg-emerald-700'
                      }`}
                    >
                      {addedId === product.id ? (
                        <>
                          <i className="ri-check-line mr-1"></i>
                          <span className="hidden sm:inline">{t('product.added')}</span>
                          <span className="sm:hidden">✓</span>
                        </>
                      ) : (
                        <>
                          <span className="hidden sm:inline">{t('product.add_to_cart')}</span>
                          <span className="sm:hidden">{t('product.add')}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
